import { useCallback, useState } from 'react'

import { useQuery } from '@sequencehq/api'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import { MINUTES } from '@sequencehq/utils/dates'

import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import type { QuoteContact } from 'Cube/domain/cube.domain.types'
import {
  type EditableRecipientType,
  type QuoteRecipientRow,
  isSigningRow,
  recipientEmailsFor,
  useQuoteRecipientRows
} from 'Cube/view/layouts/quote/components/QuoteRecipientsNotifyList'
import { AMENDMENT_OUT_FOR_SIGNATURE_SIGNERS_NOTE } from 'Cube/view/layouts/quote/AmendmentOutForSignatureBanner/AmendmentOutForSignatureBanner.constants'

export type PublishRecipients = {
  rows: QuoteRecipientRow[]
  selectedIds: string[]
  setSelectedIds: (selectedIds: string[]) => void
  showCounterSignerLastNote: boolean
  onTypeChange: (rowId: string, type: EditableRecipientType) => void
  /** The ticked rows resolved to the emails the publish endpoint takes. */
  emails: string[]
  /**
   * Somebody has been moved between signer and cc in here, so every save from
   * now on was started by this modal.
   */
  hasEditedTypes: boolean
  /** Set while signers can't be ticked, saying why. */
  signersUnavailableNote?: string
}

/**
 * The recipients the publish modal offers to share with.
 *
 * The rows come from the quote being edited rather than the published variant,
 * because this list is about who the version in front of you would go to - and
 * the modal itself can change that by moving someone between signer and cc.
 */
export const usePublishRecipients = ({
  isApprovalRequired
}: {
  isApprovalRequired: boolean
}): PublishRecipients => {
  const ctx = useCubeContext()
  const quote = ctx.queries.rawData.data.quote

  const quoteSettingsRes = useQuery(
    dashboardv20240509Client.getQuoteSettings,
    undefined,
    { staleTime: 5 * MINUTES }
  )

  const { rows, showCounterSignerLastNote } = useQuoteRecipientRows(quote, {
    // Countersigners stay hidden until the setting has actually answered.
    // Offering them and then taking them away would quietly drop whoever
    // ticked one from the publish.
    notifyCounterSignerLast:
      quoteSettingsRes.data?.notifyCounterSignerLast ??
      quoteSettingsRes.isLoading
  })

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [hasEditedTypes, setHasEditedTypes] = useState(false)

  const { updateData } = ctx.mutators

  const onTypeChange = useCallback(
    (rowId: string, type: EditableRecipientType) => {
      const { contacts, ccContacts } = quote
      const isSigner = (contactId: string) =>
        contacts.some(candidate => candidate.contactId === contactId)

      if (isSigner(rowId) === (type === 'SIGNER')) {
        return
      }

      const isSignerAfterwards = (contact: QuoteContact) =>
        contact.contactId === rowId
          ? type === 'SIGNER'
          : isSigner(contact.contactId)

      // Partitioned rather than moved, so the two lists keep a sensible order
      // when they save. What the list shows doesn't depend on it - the rows
      // hold the order they were first built in.
      const everyone = [...contacts, ...ccContacts]

      setHasEditedTypes(true)
      updateData({
        quote: {
          contacts: everyone.filter(isSignerAfterwards),
          ccContacts: everyone.filter(contact => !isSignerAfterwards(contact))
        }
      })
    },
    [quote, updateData]
  )

  // Sharing with a signer makes an e-signature quote ready to sign, which can't
  // happen while another amendment holds the signing slot. An approval
  // publishes later, so it may ask for signatures unless this quote is
  // superseded. Sharing with cc recipients is always safe.
  const isSuperseded = Boolean(quote.amendment?.supersededByQuoteId)
  const canShareWithSigners =
    !quote.isSignaturesEnabled ||
    ctx.queries.availableFeatures.quote.readyToSign.available.enabled ||
    (isApprovalRequired && !isSuperseded)
  const selectableRows = canShareWithSigners
    ? rows
    : rows.filter(row => !isSigningRow(row))
  return {
    rows,
    selectedIds,
    setSelectedIds,
    showCounterSignerLastNote,
    onTypeChange,
    emails: recipientEmailsFor(selectableRows, selectedIds),
    hasEditedTypes,
    signersUnavailableNote: canShareWithSigners
      ? undefined
      : AMENDMENT_OUT_FOR_SIGNATURE_SIGNERS_NOTE
  }
}
